import { useEffect, useState, useCallback, useRef } from "react";
import type { ColourToolsState, ColourToolsActions, NonBrandResult, ColourTarget, ColorToolScope } from "../../../types";
import { useModalDismiss } from "../hooks/useModalDismiss";
import { hexToRgbStr, normalizeHex } from "../../../utils/colourUtils";
import { resolveColorToolScope, describeColorToolScopeLabel, SCOPE_OPTION_LABELS, type ResolvedColorToolScope } from "../../../utils/colorToolScope";
import { logError, scrubMessage } from "../../../../../telemetry";
import styles from "./NonBrandModal.module.css";

const NONBRAND_CONNECTOR = "deckorate";

/** Report a NonBrandModal failure through the shared telemetry pipeline
 * (PII-scrubbed) — mirrors MapPlot.tsx's local logging helper convention.
 * `tools.replace` already catches and logs its own known failure modes
 * (and sets `tools.error`) before ever resolving, so these calls only fire
 * for a genuinely unexpected exception escaping that contract — logged
 * distinctly rather than assumed to duplicate the hook's own logging. */
function logNonBrandError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: NONBRAND_CONNECTOR, reason },
  );
}

interface NonBrandModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly tools: ColourToolsState & ColourToolsActions;
}

export function NonBrandModal({ open, onClose, tools }: NonBrandModalProps) {
  const dialogRef = useModalDismiss<HTMLDivElement>(open, onClose);
  const [detectScope, setDetectScope] = useState<ColorToolScope>("current");
  const [detectFrom, setDetectFrom] = useState(1);
  const [detectTo, setDetectTo] = useState(1);
  const [detectTarget, setDetectTarget] = useState<ColourTarget>("fill");
  const detectThreshold = 30;
  const [scanScope, setScanScope] = useState<ResolvedColorToolScope | null>(null);
  const [scopeLabel, setScopeLabel] = useState(() =>
    describeColorToolScopeLabel(detectScope, detectFrom, detectTo),
  );
  const [toHexMap, setToHexMap] = useState<Record<string, string>>({});
  const [fixingItems, setFixingItems] = useState<Set<string>>(new Set());
  const hasScannedRef = useRef(false);

  // Guards state updates after an await against a fix still in flight when
  // the modal closes or the panel unmounts (React 18 no-ops a setState on
  // an unmounted component rather than warning/leaking, but a fix that
  // resolves after close would otherwise silently rewrite `fixingItems`/
  // re-trigger a scan for a view nobody can see any more).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Always-current snapshot of the scan parameters, read by runDetectScan
  // via ref rather than passed as arguments — five call sites no longer
  // need to repeat (and can't drift on) the same 5-argument tuple, and
  // runDetectScan's own identity stays stable ([tools] only) instead of
  // changing on every keystroke in the range inputs, which matters for the
  // intentionally-narrow effect deps below.
  const detectParamsRef = useRef({
    scope: detectScope,
    from: detectFrom,
    to: detectTo,
    threshold: detectThreshold,
    target: detectTarget,
  });
  detectParamsRef.current = {
    scope: detectScope,
    from: detectFrom,
    to: detectTo,
    threshold: detectThreshold,
    target: detectTarget,
  };

  const runDetectScan = useCallback(async () => {
    const { scope, from, to, threshold, target } = detectParamsRef.current;
    const { slideFrom, slideTo, currentSlideOnly = false, selectedShapesOnly = false } = resolveColorToolScope(scope, from, to);
    setScanScope({ slideFrom, slideTo, currentSlideOnly, selectedShapesOnly });
    setScopeLabel(describeColorToolScopeLabel(scope, from, to));
    setToHexMap({});
    // A chart/picture selected under "Selected Shapes" scope used to just
    // scan silently and report "0 non-brand colours" with no explanation —
    // charts/pictures are unconditionally excluded from every colour scan
    // (see COLOUR_BLOCKED_SHAPE_KINDS in colourOps.ts), so this catches it
    // up front and shows why instead.
    if (selectedShapesOnly && !(await tools.requireSupportedSelection("Non-Brand Colors"))) return;
    await tools.detectNonBrand(slideFrom, slideTo, threshold, currentSlideOnly, target, selectedShapesOnly);
  }, [tools]);

  // Auto-scan when modal opens and theme is ready; also refresh the slide
  // count so the range inputs below can be capped to the real deck.
  useEffect(() => {
    if (!open) { hasScannedRef.current = false; return; }
    if (!tools.themeColors) return;
    if (hasScannedRef.current) return;
    hasScannedRef.current = true;
    void tools.loadSlideCount();
    void runDetectScan();
  }, [open, tools.themeColors]);

  // Clamp the range down once the real slide count is known.
  useEffect(() => {
    if (tools.slideCount == null) return;
    setDetectFrom((v) => Math.min(v, tools.slideCount!));
    setDetectTo((v) => Math.min(v, tools.slideCount!));
  }, [tools.slideCount]);

  // Re-scan when scope changes (after first open). Deps intentionally list
  // only `detectScope`, not `detectFrom`/`detectTo`/`detectThreshold`/`open`/
  // `tools.themeColors`/`runDetectScan` — those are read via
  // detectParamsRef/closure on purpose, so typing in the range inputs
  // doesn't itself trigger a re-scan (only committing a scope/target change
  // does). Do not "fix" this to be exhaustive; that would re-scan on every
  // keystroke in the From/To fields.
  //
  // Slide Range / All Slides are deliberately excluded from auto-scan — a
  // full-deck (or wide-range) scan is comparatively expensive and easy to
  // trigger by accident just by clicking a radio button, so landing on
  // either one only arms it; the user must press "Scan" themselves to
  // actually run it.
  const prevScopeRef = useRef<ColorToolScope | null>(null);
  useEffect(() => {
    if (!open || !tools.themeColors) { prevScopeRef.current = detectScope; return; }
    if (prevScopeRef.current === null) { prevScopeRef.current = detectScope; return; }
    if (prevScopeRef.current === detectScope) return;
    prevScopeRef.current = detectScope;
    if (detectScope === "range" || detectScope === "all") return;
    runDetectScan();
  }, [detectScope]);

  // Re-scan when the checked asset type changes (after first open). Same
  // intentionally-narrow-deps reasoning as the scope effect above, and the
  // same Slide Range/All Slides exclusion — switching Fill/Font/Line while
  // one of those is the active scope must not silently kick off a scan either.
  const prevTargetRef = useRef<ColourTarget | null>(null);
  useEffect(() => {
    if (!open || !tools.themeColors) { prevTargetRef.current = detectTarget; return; }
    if (prevTargetRef.current === null) { prevTargetRef.current = detectTarget; return; }
    if (prevTargetRef.current === detectTarget) return;
    prevTargetRef.current = detectTarget;
    if (detectScope === "range" || detectScope === "all") return;
    runDetectScan();
  }, [detectTarget]);

  const handleFixItem = useCallback(
    async (r: NonBrandResult) => {
      if (!scanScope || !r.nearest) return;
      const key = `${r.hex}-${r.colorType}`;
      const toHex = toHexMap[key] ?? r.nearest.hex;
      setFixingItems((prev) => new Set(prev).add(key));
      try {
        await tools.replace(
          [{ from: r.hex, to: toHex }],
          new Set<ColourTarget>([r.colorType]),
          scanScope.slideFrom,
          scanScope.slideTo,
          scanScope.currentSlideOnly,
          scanScope.selectedShapesOnly,
        );
        if (isMountedRef.current) runDetectScan();
      } catch (e) {
        logNonBrandError("nonbrand-fix-item-unexpected", "NonBrandFixItemError", e);
      } finally {
        if (isMountedRef.current) {
          setFixingItems((prev) => {
            const s = new Set(prev);
            s.delete(key);
            return s;
          });
        }
      }
    },
    [scanScope, toHexMap, tools, runDetectScan],
  );

  const handleFixAll = useCallback(async () => {
    if (!scanScope) return;
    // Group by colorType and run one replace pass per type
    const byType = new Map<ColourTarget, Array<{ from: string; to: string }>>();
    for (const r of tools.nonBrandResults) {
      if (!r.nearest) continue;
      const key = `${r.hex}-${r.colorType}`;
      const toHex = toHexMap[key] ?? r.nearest.hex;
      const arr = byType.get(r.colorType) ?? [];
      arr.push({ from: r.hex, to: toHex });
      byType.set(r.colorType, arr);
    }
    if (byType.size === 0) return;

    // One bad colour-type group shouldn't abort the rest — continue
    // attempting every remaining group (matches this codebase's established
    // "one bad shape can't abort the batch" convention, e.g. colourOps.ts),
    // then report which groups (if any) failed.
    const failedTypes: ColourTarget[] = [];
    for (const [colorType, mappings] of byType) {
      try {
        await tools.replace(
          mappings,
          new Set<ColourTarget>([colorType]),
          scanScope.slideFrom,
          scanScope.slideTo,
          scanScope.currentSlideOnly,
          scanScope.selectedShapesOnly,
        );
      } catch (e) {
        failedTypes.push(colorType);
        logNonBrandError(`nonbrand-fix-all-${colorType}-unexpected`, "NonBrandFixAllError", e);
      }
    }
    if (failedTypes.length > 0) {
      logNonBrandError(
        `nonbrand-fix-all-partial-failure-${failedTypes.length}-of-${byType.size}`,
        "NonBrandFixAllPartialFailure",
        new Error(`Fix All failed for: ${failedTypes.join(", ")}`),
      );
    }
    if (isMountedRef.current) runDetectScan();
  }, [scanScope, tools, toHexMap, runDetectScan]);

  if (!open) return null;

  const totalOccurrences = tools.nonBrandResults.reduce((s, r) => s + r.count, 0);
  const fixableCount = tools.nonBrandResults.filter((r) => r.nearest != null).length;

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog} ref={dialogRef}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>Non-Brand Colors</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close" data-testid="nonbrand-close">
            ×
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Asset type selector — card-style rows, matching Replace's "Select what to replace" */}
          <div>
            <div className={styles.sectionLabel}>Select what to check</div>
            <div className={styles.radioGroup}>
              {(["fill", "font", "line"] as ColourTarget[]).map((t) => (
                <label
                  key={t}
                  className={`${styles.radioRow} ${detectTarget === t ? styles.radioRowActive : ""}`}
                >
                  <input
                    type="radio"
                    name="nbDetectTarget"
                    value={t}
                    checked={detectTarget === t}
                    onChange={() => setDetectTarget(t)}
                    data-testid={`nonbrand-target-${t}`}
                  />
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </label>
              ))}
            </div>
            <div className={styles.noteText}>
              Colors auto-detected from the selected scope below.
            </div>
          </div>

          {/* Scope selector */}
          <div>
            <div className={styles.sectionLabel}>Apply to</div>
            <div className={styles.scopeRow}>
              {(["current", "range", "all"] as const).map((s) => (
                <label key={s} className={styles.scopeOption}>
                  <input
                    type="radio"
                    name="nbDetectScope"
                    value={s}
                    checked={detectScope === s}
                    onChange={() => setDetectScope(s)}
                    data-testid={`nonbrand-scope-${s}`}
                  />
                  {SCOPE_OPTION_LABELS[s]}
                </label>
              ))}
            </div>
            {detectScope === "range" && (
              <div className={styles.slideRangeRow}>
                <span>From:</span>
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setDetectFrom((v) => Math.max(1, v - 1))}
                  data-testid="nonbrand-from-decrement"
                >−</button>
                <input
                  type="text"
                  className={styles.rangeInput}
                  value={String(detectFrom).padStart(2, "0")}
                  onChange={(e) => {
                    const n = Number.parseInt(e.target.value, 10);
                    if (!Number.isNaN(n) && n >= 1) {
                      const capped = tools.slideCount != null ? Math.min(n, tools.slideCount) : n;
                      // Never let "From" land above "To" — mirrors the
                      // n >= detectFrom guard already on "To"'s own onChange.
                      setDetectFrom(Math.min(capped, detectTo));
                    }
                  }}
                  data-testid="nonbrand-from-input"
                />
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setDetectFrom((v) => {
                    const next = tools.slideCount != null ? Math.min(tools.slideCount, v + 1) : v + 1;
                    return Math.min(next, detectTo);
                  })}
                  disabled={tools.slideCount != null && detectFrom >= tools.slideCount}
                  data-testid="nonbrand-from-increment"
                >+</button>
                <span>to</span>
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setDetectTo((v) => Math.max(detectFrom, v - 1))}
                  data-testid="nonbrand-to-decrement"
                >−</button>
                <input
                  type="text"
                  className={styles.rangeInput}
                  value={String(detectTo).padStart(2, "0")}
                  onChange={(e) => {
                    const n = Number.parseInt(e.target.value, 10);
                    if (!Number.isNaN(n) && n >= detectFrom) {
                      setDetectTo(tools.slideCount != null ? Math.min(n, tools.slideCount) : n);
                    }
                  }}
                  data-testid="nonbrand-to-input"
                />
                <button
                  type="button"
                  className={styles.pmBtn}
                  onClick={() => setDetectTo((v) => (tools.slideCount != null ? Math.min(tools.slideCount, v + 1) : v + 1))}
                  disabled={tools.slideCount != null && detectTo >= tools.slideCount}
                  data-testid="nonbrand-to-increment"
                >+</button>
              </div>
            )}
          </div>

          {/* Progress */}
          {tools.detecting && (
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${tools.detectProgress}%` }} />
            </div>
          )}

          {/* Summary */}
          {!tools.detecting && tools.nonBrandResults.length > 0 && (
            <div className={styles.summary}>
              {totalOccurrences} occurrence{totalOccurrences !== 1 ? "s" : ""} in{" "}
              {tools.nonBrandResults.length} color{tools.nonBrandResults.length !== 1 ? "s" : ""} — {scopeLabel}
            </div>
          )}

          {/* Results list */}
          {!tools.detecting && tools.nonBrandResults.length > 0 && (
            <div className={styles.resultList}>
              {tools.nonBrandResults.map((r) => {
                const key = `${r.hex}-${r.colorType}`;
                const toHex = toHexMap[key] ?? r.nearest?.hex ?? r.hex;
                const isFixing = fixingItems.has(key);
                return (
                  <div key={key} className={styles.resultItem}>
                    <span className={styles.fromSwatch} style={{ background: "#" + r.hex }} />
                    <div className={styles.info}>
                      <span className={styles.rgbText}>{hexToRgbStr(r.hex)}</span>
                      <span className={styles.meta}>
                        {r.colorType} · {r.count} shape{r.count !== 1 ? "s" : ""}
                        {r.nearest ? ` · ${r.nearest.key}` : ""}
                      </span>
                    </div>
                    <span className={styles.arrow}>→</span>
                    {r.nearest ? (
                      <div className={styles.targetGroup}>
                        <span className={styles.toSwatch} style={{ background: "#" + toHex }} />
                        <input
                          type="color"
                          className={styles.colorPicker}
                          value={"#" + toHex}
                          title="Pick replacement color"
                          data-testid={`nonbrand-target-color-${key}`}
                          onChange={(e) => {
                            const hex = normalizeHex(e.target.value);
                            if (hex) setToHexMap((prev) => ({ ...prev, [key]: hex }));
                          }}
                        />
                      </div>
                    ) : (
                      <span className={styles.noNearest}>no match</span>
                    )}
                    <button
                      type="button"
                      className={styles.fixBtn}
                      disabled={isFixing || tools.busy || !r.nearest}
                      onClick={() => handleFixItem(r)}
                      data-testid={`nonbrand-fix-${key}`}
                    >
                      {isFixing ? "…" : "Fix"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {!tools.detecting && tools.nonBrandResults.length === 0 && tools.detectProgress === 100 && (
            <div className={styles.empty}>All colors on {scopeLabel} are on-brand ✓</div>
          )}

          {/* Busy / done / error */}
          {tools.busy && (
            <div className={styles.spinnerRow}>
              <span className={styles.spinner} />{" "}
              Applying…
              {tools.replaceProgress > 0 && tools.replaceProgress < 100 && (
                <span style={{ marginLeft: 6, fontSize: "0.85em", opacity: 0.75 }}>
                  {tools.replaceProgress}%
                </span>
              )}
            </div>
          )}
          {tools.error && !tools.busy && (
            <div className={styles.errorNotice} role="alert" data-testid="nonbrand-error">
              {tools.error}
            </div>
          )}
          {tools.replaceDone !== null && !tools.busy && !tools.error && (
            <div className={styles.doneNotice}>
              Replaced {tools.replaceDone} instance(s).
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnScan}
            onClick={() => {
              hasScannedRef.current = false;
              runDetectScan();
              hasScannedRef.current = true;
            }}
            disabled={tools.detecting || tools.busy || !tools.themeColors}
            title="Re-scan colors on the selected scope"
            data-testid="nonbrand-scan"
          >
            {tools.detecting ? "Scanning…" : "↺ Scan"}
          </button>
          <button type="button" className={styles.btnClose} onClick={onClose} data-testid="nonbrand-close-footer">
            Close
          </button>
          <button
            type="button"
            className={styles.btnFixAll}
            onClick={handleFixAll}
            disabled={tools.busy || tools.detecting || fixableCount === 0}
            data-testid="nonbrand-fix-all"
          >
            Fix All ({fixableCount})
          </button>
        </div>
      </div>
    </div>
  );
}
